import axios from 'axios'
import Settings from '@/app/config/appConfig'
import { redirectToLogin, userManager } from '@/config'

// Default timeout for all API requests (in milliseconds)
const TIMEOUT = 300000

// Shared promise to deduplicate concurrent refresh calls
let refreshPromise: Promise<string | null> | null = null

/**
 * Returns a valid access token, refreshing silently if expired/expiring.
 * Deduplicates concurrent refresh attempts via a shared promise.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const user = await userManager.getUser()
  if (!user?.access_token) return null

  const bufferSeconds = 30
  const nowInSeconds = Math.floor(Date.now() / 1000)

  if (user.expires_at && user.expires_at - nowInSeconds > bufferSeconds) {
    return user.access_token
  }

  // Token is expired or expiring soon — refresh
  if (!refreshPromise) {
    refreshPromise = userManager
      .signinSilent()
      .then((refreshedUser) => refreshedUser?.access_token ?? null)
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

const COMPANY_ID_IN_URL =
  /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/

// Hands a company-scoped failure to GlobalErrorHandler, which owns the modal. Exported
// because the two legacy service layers (app/services/baseService and
// modules/printouts/services/BaseService) call bare axios and never reach the interceptor
// below — without this they recognise the error code and then swallow it, leaving an
// empty screen where the modal should be.
export function dispatchCompanyErrorModal(errorCode: string, error: any) {
  // The request URL embeds the companyId: /api/{guid}/...
  // Extract it so the migration-in-progress UI knows which company to watch.
  const guidMatch: RegExpMatchArray | null = (error?.config?.url || '').match(
    COMPANY_ID_IN_URL,
  )
  window.dispatchEvent(
    new CustomEvent('globalApiError', {
      detail: { errorCode, data: { companyId: guidMatch ? guidMatch[0] : null } },
    }),
  )
}

// 403 IP_NOT_ALLOWED — firma ma listę dozwolonych adresów IP, a bieżącego adresu na niej nie ma.
// Zwraca true, gdy błąd został obsłużony modalem (także dla starych warstw serwisów).
export function dispatchIpNotAllowedModal(error: any): boolean {
  if (error?.response?.status !== 403 || error.response.data?.code !== 'IP_NOT_ALLOWED')
    return false
  window.dispatchEvent(
    new CustomEvent('globalApiError', {
      detail: { errorCode: 'IP_NOT_ALLOWED', data: error.response.data },
    }),
  )
  return true
}

// Create a shared Axios instance
export const axiosInstance = axios.create({
  baseURL: Settings.serverAddress,
  timeout: TIMEOUT,
})

// Request interceptor: add Authorization header with a valid Keycloak token
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await getValidAccessToken()
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      }
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor: retry on 401 after silent refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Handle 402 Payment Required — show the modal both for unpaid invoices
    // ("unpaid") and for companies that have no record in the external payment
    // panel at all ("not_in_panel", a 404 from the panel that the backend treats
    // as unpaid). The modal branches on the status to show the right message.
    if (
      error.response?.status === 402 &&
      (error.response?.data?.status === 'unpaid' ||
        error.response?.data?.status === 'not_in_panel')
    ) {
      window.dispatchEvent(
        new CustomEvent('globalApiError', {
          detail: { errorCode: 'PAYMENT_REQUIRED', data: error.response.data },
        }),
      )
      return Promise.reject(error)
    }

    // 402 SUBSCRIPTION_EXPIRED — Stripe subscription lapsed or company blocked.
    // 402 SUBSCRIPTION_REQUIRED — brand-new tenant that has not paid yet (no
    // expiration ever set). Distinct path so we don't tell new users their
    // never-existing subscription "expired".
    const sub402 =
      error.response?.status === 402 &&
      (error.response?.data?.code === 'SUBSCRIPTION_EXPIRED' ||
        error.response?.data?.code === 'SUBSCRIPTION_REQUIRED')
    if (sub402) {
      const code = error.response.data.code as 'SUBSCRIPTION_EXPIRED' | 'SUBSCRIPTION_REQUIRED'
      window.dispatchEvent(
        new CustomEvent('globalApiError', {
          detail: { errorCode: code, data: error.response.data },
        }),
      )
      if (typeof window !== 'undefined') {
        const onSubPage = window.location.pathname.startsWith('/subscription/')
        const onCheckout = window.location.pathname.startsWith('/checkout')
        if (code === 'SUBSCRIPTION_REQUIRED') {
          // New tenant — push to plan picker, not the "expired" page.
          if (!onCheckout) window.location.href = '/checkout'
        } else if (!onSubPage) {
          window.location.href = '/subscription/expired'
        }
      }
      return Promise.reject(error)
    }

    // 402 OVER_DRIVER_LIMIT — the tenant exceeded the IncludedDrivers ceiling
    // of their plan and the server blocked a billable event. Show a modal
    // with the plan/limit details + a CTA to "Zmień plan"; do NOT navigate
    // away because the user is mid-operation (import / calc / printout) and
    // we want them to land back on their screen after upgrading.
    if (
      error.response?.status === 402 &&
      (error.response?.data?.code === 'OVER_DRIVER_LIMIT' ||
        error.response?.data?.code === 'NO_ACTIVE_PLAN')
    ) {
      window.dispatchEvent(
        new CustomEvent('globalApiError', {
          detail: { errorCode: error.response.data.code, data: error.response.data },
        }),
      )
      return Promise.reject(error)
    }

    if (dispatchIpNotAllowedModal(error)) return Promise.reject(error)

    // Preserve existing NOT_MIGRATED_COMPANY handling; COMPANY_HAS_NO_DATABASE rides the
    // same path — a service company reached by URL (the sidebar and the picker no longer
    // lead there) has no database at all, so the answer is "switch company", not "wait".
    const errorCode = error.response?.data?.ErrorCode
    if (
      errorCode === 'NOT_MIGRATED_COMPANY' ||
      errorCode === 'COMPANY_HAS_NO_DATABASE'
    ) {
      dispatchCompanyErrorModal(errorCode, error)
      return Promise.reject(error)
    }

    // 401 retry logic — only attempt once per request
    if (error.response?.status === 401 && !originalRequest._authRetried) {
      originalRequest._authRetried = true
      try {
        const refreshedUser = await userManager.signinSilent()
        if (refreshedUser?.access_token) {
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${refreshedUser.access_token}`,
          }
          return axiosInstance(originalRequest)
        }
      } catch {
        // Silent refresh failed — redirect to Keycloak login
        redirectToLogin()
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  },
)
